import React from "react";
import styled from "styled-components/macro";
import { CircularProgress, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { faCheck, faLightbulbOn, faRefresh } from "@duocards/pro-regular-svg-icons";
import { faTranslate } from "../../icons/CustomIcons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const SIconButton = styled(IconButton)`
  background-color: ${({ theme }) => theme.duo.color.white};
  width: 32px;
  height: 32px;
  margin-right: 10px;
  border-radius: 50%;

  &:hover {
    background-color: ${({ theme }) => theme.duo.color.white};
  }

  &:active {
    background-color: ${({ theme }) => theme.duo.color.white} !important;
  }
`;

const SFaIcon = styled(FontAwesomeIcon)`
  font-size: 16px;
`;

const SCircularProgress = styled(CircularProgress)`
  color: ${({ theme }) => theme.duo.color.lightBlue};
  width: 20px !important;
  height: 20px !important;
  margin-right: 10px;
`;

type Props = {
  isLoading: boolean;
  score?: number | null;
  needRetry?: boolean;
  onClick?: () => void;
  mismatch?: boolean;
};

const SCORE_THRESHOLD = 0.7;

export const isGoodGrammar = (score: number) => score > SCORE_THRESHOLD;

const GrammarFeedbackIcon: React.FC<Props> = ({ isLoading, score, onClick, mismatch, needRetry }) => {
  const theme = useTheme();
  if (isLoading) {
    return <SCircularProgress size={24} />;
  }

  if (needRetry) {
    return (
      <SIconButton onClick={onClick}>
        <SFaIcon icon={faRefresh} color={theme.duo.color.lightBlue} />
      </SIconButton>
    );
  }

  if (mismatch) {
    return (
      <SIconButton onClick={onClick}>
        <SFaIcon icon={faTranslate} color={theme.duo.color.lightBlue} />
      </SIconButton>
    );
  }

  if (typeof score === "number" && !isNaN(score)) {
    const isGood = isGoodGrammar(score);
    return (
      <SIconButton onClick={onClick} disabled={isGood}>
        <SFaIcon
          icon={isGood ? faCheck : faLightbulbOn}
          color={isGood ? theme.palette.success.main : theme.palette.warning.main}
        />
      </SIconButton>
    );
  }

  return null;
};

export default GrammarFeedbackIcon;
